import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Modal, Select } from "antd";
import { GET_COMPANY_USERS } from "../../graphql/companyQueries";
import { ASSIGN_OPEN_HOUSE_HOST } from "../../graphql/listingQueries";
import { useToast } from "../Toast/toastHook";
import { formatAgentName } from "../upcomingListings/listingDisplay";
import { formatSlotTime } from "./openHouseDisplay";

const UNASSIGNED = "";

/**
 * Books an agent onto one open-house slot, or clears the booking.
 *
 * The agent list is the company's own active users — the same source the
 * Upcoming Listings agent filter uses — because a host has to be someone the
 * broker can actually send.
 */
const AssignHostModal = ({ companyId, occurrence, onClose, onAssigned }) => {
  const toast = useToast(4000);
  const open = Boolean(occurrence);

  const [hostId, setHostId] = useState(UNASSIGNED);

  // Reopening on a different slot must not inherit the previous selection.
  useEffect(() => {
    setHostId(occurrence?.host?.id ?? UNASSIGNED);
  }, [occurrence]);

  const { data: usersData } = useQuery(GET_COMPANY_USERS, {
    variables: { companyId, sorted: true },
    fetchPolicy: "cache-first",
    skip: !open,
  });

  const options = useMemo(() => {
    const users = (usersData?.getCompanyUsers ?? [])
      .filter((u) => !u.deletedAt && u.isActive)
      .map((u) => ({ value: u.id, label: formatAgentName(u) || u.email }))
      .filter((o) => o.label);

    return [{ value: UNASSIGNED, label: "— No host —" }, ...users];
  }, [usersData]);

  const [assign, { loading }] = useMutation(ASSIGN_OPEN_HOUSE_HOST, {
    onCompleted() {
      const name = options.find((o) => o.value === hostId)?.label;
      toast(
        "success",
        hostId ? `${name} assigned to host` : "Host cleared"
      );
      onAssigned?.();
      onClose();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const save = () =>
    assign({
      variables: {
        listingId: occurrence.listingId,
        openHouseId: occurrence.openHouseId,
        // The server reads null as "unassign"; "" would fail id casting.
        hostId: hostId === UNASSIGNED ? null : hostId,
      },
    });

  return (
    <Modal
      open={open}
      title="Assign host"
      onCancel={onClose}
      okText="Assign"
      confirmLoading={loading}
      onOk={save}
      okButtonProps={{
        style: { backgroundColor: "#96aa10", borderColor: "#96aa10" },
      }}
      destroyOnClose
    >
      {occurrence && (
        <>
          <div className="mb-4 rounded-sm border border-light_gray bg-[#f7f8f4] px-3 py-2">
            <div className="text-sm font-bold text-secondary">
              {occurrence.listing?.streetAddr}
            </div>
            <div className="text-xs text-gray-400">
              {[formatSlotTime(occurrence), occurrence.listing?.city]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>

          <label
            htmlFor="open-house-host"
            className="mb-1 block text-xs font-bold uppercase tracking-wide text-secondary"
          >
            Host
          </label>
          <Select
            id="open-house-host"
            showSearch
            optionFilterProp="label"
            value={hostId}
            onChange={setHostId}
            options={options}
            style={{ width: "100%" }}
            aria-label="Host"
          />
        </>
      )}
    </Modal>
  );
};

export default AssignHostModal;
